import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { task } from 'ember-concurrency';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import clsx from 'clsx';

import { ASSIGNMENT } from 'sh-www/models/assignment';

const ASSIGNMENT_PAGE_LIMIT = 15;

const FILTERS = {
  BLASTED: 'blasted',
  COMPANY: 'company',
  DIRECT: 'direct',
  PRIVACY_LEVEL: 'privacy_level',
  SELECT: 'select',
  STATUS: 'status',
  TITLE: 'title',
  // Advanced Filters (require multiple inputs)
  APPLICANT_COUNT: 'applicant_count',
  APPLICANT_COUNT_COMPARATOR: 'applicant_count_comparator',
  CREATED_AT_DATE: 'created_at',
  CREATED_AT_DATE_COMPARATOR: 'created_at_comparator',
  LIVE_AT_DATE: 'live_at',
  LIVE_AT_DATE_COMPARATOR: 'live_at_comparator',
  DEADLINE_DAYS_REMAINING: 'deadline_days_remaining',
  DEADLINE_HOURS_REMAINING: 'deadline_hours_remaining',
};

const SORTS = {
  UPDATED_AT: 'updated_at',
  CREATED_AT: 'created_at',
};

const SORT_NAMES = {
  [SORTS.UPDATED_AT]: 'Last updated',
  [SORTS.CREATED_AT]: 'Created',
};

const SORT_ORDERS = {
  ASC: 'asc',
  DESC: 'desc',
};

export const ADMIN_POSTINGS_CONTROLLER = {
  FILTERS,
  FILTER_VALUES: Object.values(FILTERS),
  SORTS,
  SORT_NAMES,
  SORT_ORDERS,
};

const CLASS_NAME = 'SH-r-admin-postings';

class AdminPostingsController extends Controller {
  @service constants;
  @service store;
  @service shData;

  queryParams = [
    'page',
    'sort',
    'sortOrder',
    ...ADMIN_POSTINGS_CONTROLLER.FILTER_VALUES,
  ];

  page = 1;
  _pagination = null;
  sort = ADMIN_POSTINGS_CONTROLLER.SORTS.CREATED_AT;
  sortOrder = ADMIN_POSTINGS_CONTROLLER.SORT_ORDERS.DESC;

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME,
    );
  }

  get className() {
    return CLASS_NAME;
  }

  @computed(...ADMIN_POSTINGS_CONTROLLER.FILTER_VALUES)
  get queryValues() {
    const values = Object.values(ADMIN_POSTINGS_CONTROLLER.FILTERS).reduce((object, key) => {
      object[key] = this[key];
      return object;
    }, {});

    // Re-map boolean values
    Object.keys(values).forEach((key) => {
      if (values[key] === 'false') {
        values[key] = false;
      } else if (values[key] === 'true') {
        values[key] = true;
      }
    })

    return values;
  }

  @computed('loadPostings.isRunning','_pagination.totalPages')
  get showPagination() {
    return !this.loadPostings.isRunning
      && this._pagination?.totalPages > 1;
  }

  @task
  *loadPostings() {
    const filters = { ...this.queryValues };

    // This is a default value
    if (filters[ADMIN_POSTINGS_CONTROLLER.FILTERS.STATUS] === undefined) {
      filters[ADMIN_POSTINGS_CONTROLLER.FILTERS.STATUS] = ASSIGNMENT.STATUS.LIVE;
    }

    const params = {
      filter: filters,
      include: 'assignment-deliverables,assignment-schedules,brand,blasts,buyer.company,candidate-location,location,services',
      sort: this.sort,
      sortOrder: this.sortOrder,
      count: ASSIGNMENT_PAGE_LIMIT,
      format: ASSIGNMENT.FORMATS.ADMIN,
    };

    const page = this.page;
    params.page = page || 1;

    try {
      const postings = yield this.store.query('assignment', params);
      this.set('model.postings', postings);
      this.set('_pagination', this.shData.getPaginationModelFromResponse(postings));
    } catch (e) {
      handleErrorResponseWithModal(this, e);
    }
  }

  @action
  handlePageChange(increment) {
    this.set('page', this.page + increment);
  }

  @action
  onSetQueryValues(keyValuePairs) {
    const filters = Object.values(ADMIN_POSTINGS_CONTROLLER.FILTERS).reduce((object, key) => {
      object[key] = undefined;
      return object;
    }, {});

    Object.keys(keyValuePairs).map((key) => {
      // Set nulls to undefined or else they'll still be used
      if (keyValuePairs[key] === null) {
        filters[key] = undefined;
      } else {
        filters[key] = keyValuePairs[key];
      }
    });

    this.setProperties(filters);
  }

  @action
  reset() {
    const filtersToReset = Object.values(ADMIN_POSTINGS_CONTROLLER.FILTERS).reduce((object, key) => {
      object[key] = undefined;
      return object;
    }, {});

    this.setProperties({
      page: 1,
      sort: ADMIN_POSTINGS_CONTROLLER.SORTS.CREATED_AT,
      sortOrder: ADMIN_POSTINGS_CONTROLLER.SORT_ORDERS.DESC,
      ...filtersToReset
    });
  }
}

export default AdminPostingsController;
